import React from 'react';
import { Icon, IconName } from './Icon';

export type InspectionStatus = 'ok' | 'rec' | 'noaccess' | 'deny' | 'todo';

export interface StatusMetaEntry {
  label: string;
  icon: IconName;
  color: string;
  bar: string;
  bg: string;
}

/**
 * Canonical inspection statuses. Always rendered as icon + color + text
 * (never color alone — required for the color-blind / low-light field use).
 */
export const STATUS_META: Record<InspectionStatus, StatusMetaEntry> = {
  ok:       { label: 'Bez uwag',     icon: 'check-circle',   color: 'var(--status-ok)',   bar: 'var(--status-ok-bar)',   bg: 'var(--status-ok-bg)' },
  rec:      { label: 'Zalecenie',    icon: 'triangle-alert', color: 'var(--status-rec)',  bar: 'var(--status-rec-bar)',  bg: 'var(--status-rec-bg)' },
  noaccess: { label: 'Brak dostępu', icon: 'lock',           color: 'var(--status-deny)', bar: 'var(--status-deny-bar)', bg: 'var(--status-deny-bg)' },
  deny:     { label: 'Odmowa',       icon: 'ban',            color: 'var(--status-deny)', bar: 'var(--status-deny-bar)', bg: 'var(--status-deny-bg)' },
  todo:     { label: 'Do zrobienia', icon: 'clock',          color: 'var(--status-todo)', bar: 'var(--status-todo-bar)', bg: 'var(--status-todo-bg)' },
};

export interface StatusChipProps {
  /** Status id. Default 'todo'. */
  status?: InspectionStatus;
  /** Override the default Polish label. */
  label?: string;
  /** Size tier. Default 'md'. */
  size?: 'sm' | 'md' | 'lg';
  /** Render the tinted pill background. Default true. */
  withBg?: boolean;
  style?: React.CSSProperties;
}

/**
 * StatusChip — pill carrying icon + label + status color.
 * size: 'sm' (inline meta), 'md' (default), 'lg' (card header chip).
 */
export function StatusChip({ status = 'todo', label, size = 'md', withBg = true, style, ...rest }: StatusChipProps) {
  const meta = STATUS_META[status] || STATUS_META.todo;
  const dims = {
    sm: { font: 'var(--text-label)', icon: 20, pad: '4px 10px', gap: 6 },
    md: { font: 'var(--text-label)', icon: 24, pad: '8px 14px', gap: 8 },
    lg: { font: 'var(--text-body)',  icon: 26, pad: '10px 18px', gap: 10 },
  }[size];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: dims.gap,
        padding: dims.pad,
        fontFamily: 'var(--font-sans)',
        fontSize: dims.font,
        fontWeight: 'var(--weight-semibold)',
        lineHeight: 1,
        color: meta.color,
        background: withBg ? meta.bg : 'transparent',
        borderRadius: 'var(--radius-pill)',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      <Icon name={meta.icon} size={dims.icon} />
      <span>{label || meta.label}</span>
    </span>
  );
}
